/**
 * Encryption and decryption utilities using Web Crypto API
 */

// Password hash - this is SHA-256 hash of "headintheclouds"
export const PASSWORD_HASH = "6f7c9e78f00fb2f703d4220ee0ebc4effdd3646dac4d4d26c8483a046d2814dd";

// The fixed encrypted password key for the application
const ENCRYPTED_KEY = 'WctJVk6SSdcKvuQz5/ElbQ==.FaXtHgfEI3/m77M1.7342faw3OG0ezvG8Nv1MW3rp/vUXdOjH1IAJNIh950zbtoK30LCUuFqaa2OqeDDUKmp9mlB4uEaSZNQ5JA07PE+vLJxEO0QM8TefHNpTkn0iq7TRPkY5hgUghU5r0RIuegv6N7XZNkDTvVnxS7Cdq1v3fLANPsKvFegwu8lxbxYRto9ynhWNSNfEyg9pG5wSZ5gItS+q31oPwduq4xLQzWKi6reV0bT+hu4IdWKBMqhBV97/RLKdLyPI7MS3gTVFV56yYZ5vksbCOWVWeyiAYrCdErZvy82Nu+vm9RPLUz9fR9BEnGa/ItF4LzN3xQZ4ksEAUIoDPbOZv777KBL00DwrjDgNIb+tEBUOVg6LRKmk6anGGEujj6dyMnsHP/GIncGecqZvwA==';

// The Turso database URL
export const TURSO_URL = 'libsql://calculator-tristenharr.aws-us-west-2.turso.io';

/**
 * Converts a string to an ArrayBuffer
 * @param {string} str - String to convert
 * @returns {Uint8Array} - Resulting array buffer
 */
const str2ab = (str) => {
  const encoder = new TextEncoder();
  return encoder.encode(str);
};

/**
 * Converts an ArrayBuffer to a string
 * @param {ArrayBuffer} buf - Buffer to convert
 * @returns {string} - Resulting string
 */
const ab2str = (buf) => {
  const decoder = new TextDecoder();
  return decoder.decode(buf);
};

/**
 * Converts an ArrayBuffer to a Base64 string
 * @param {ArrayBuffer} buffer - Buffer to convert
 * @returns {string} - Base64 string
 */
const bufferToBase64 = (buffer) => {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)));
};

/**
 * Converts a Base64 string to an ArrayBuffer
 * @param {string} base64 - Base64 string
 * @returns {ArrayBuffer} - Resulting buffer
 */
const base64ToBuffer = (base64) => {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
};

/**
 * Derives a key from a password
 * @param {string} password - The password to derive the key from
 * @param {ArrayBuffer} salt - The salt for key derivation
 * @returns {Promise<CryptoKey>} - The derived key
 */
const deriveKey = async (password, salt) => {
  const passwordKey = await window.crypto.subtle.importKey(
    'raw',
    str2ab(password),
    { name: 'PBKDF2' },
    false,
    ['deriveKey']
  );

  return window.crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt,
      iterations: 100000,
      hash: 'SHA-256'
    },
    passwordKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
};

/**
 * Encrypts text using AES-GCM with a secure key derivation
 * @param {string} text - Text to encrypt
 * @param {string} password - Password used to derive the encryption key
 * @returns {Promise<string>} - Base64 encoded string containing salt, IV, and ciphertext
 */
export const encrypt = async (text, password) => {
  // Generate a random salt
  const salt = window.crypto.getRandomValues(new Uint8Array(16));

  // Generate a random IV
  const iv = window.crypto.getRandomValues(new Uint8Array(12));

  // Derive key from password
  const key = await deriveKey(password, salt);

  // Encrypt the text
  const encrypted = await window.crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv
    },
    key,
    str2ab(text)
  );

  // Combine everything into a single string
  // Format: base64(salt) + '.' + base64(iv) + '.' + base64(ciphertext)
  return [
    bufferToBase64(salt),
    bufferToBase64(iv),
    bufferToBase64(encrypted)
  ].join('.');
};

/**
 * Decrypts text that was encrypted with the encrypt function
 * @param {string} encryptedData - Encrypted data from the encrypt function
 * @param {string} password - Password used for encryption
 * @returns {Promise<string>} - Decrypted text
 * @throws Error if decryption fails (wrong password or tampered data)
 */
export const decrypt = async (encryptedData, password) => {
  // Split the encrypted data
  const parts = encryptedData.split('.');
  if (parts.length !== 3) {
    throw new Error('Invalid encrypted data format');
  }

  const salt = base64ToBuffer(parts[0]);
  const iv = base64ToBuffer(parts[1]);
  const ciphertext = base64ToBuffer(parts[2]);

  // Derive the key from password
  const key = await deriveKey(password, salt);

  try {
    // Decrypt
    const decrypted = await window.crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: new Uint8Array(iv)
      },
      key,
      ciphertext
    );

    return ab2str(decrypted);
  } catch (error) {
    throw new Error('Decryption failed: Invalid password or data has been tampered with');
  }
};

/**
 * Hash a password string using SHA-256
 * @param {string} password - The password to hash
 * @returns {Promise<string>} - The hexadecimal hash value
 */
export const hashPassword = async (password) => {
  // Encode the password string as UTF-8
  const encoder = new TextEncoder();
  const data = encoder.encode(password);

  // Hash the password using SHA-256
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);

  // Convert the hash to a hexadecimal string
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

  return hashHex;
};

/**
 * Verify a password against the stored hash
 * @param {string} password - The password to verify
 * @returns {Promise<boolean>} - Whether the password is correct
 */
export const verifyPassword = async (password) => {
  const hash = await hashPassword(password);
  return hash === PASSWORD_HASH;
};

/**
 * Get the decrypted auth token for the database
 * @param {string} password - Password to use for decryption
 * @returns {Promise<string>} - The decrypted database auth token
 */
export const getAuthToken = async (password) => {
  try {
    // Use the provided password to decrypt the token
    const decryptedToken = await decrypt(ENCRYPTED_KEY, password);
    return decryptedToken;
  } catch (error) {
    console.error('Failed to decrypt auth token:', error);
    throw new Error('Failed to decrypt database authentication token');
  }
};